import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const receiveShipmentResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

/**
 * Schema for received items in the request.
 * Uses unified itemId for both parts and products.
 */
const receiveItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: z.number().int().positive(),
  externalReference: z.string().optional(),
  serials: z.array(z.string()).optional(),
});

export const receiveShipmentSchema = z.object({
  receiveLocationId: z.string(),
  items: z.array(receiveItemSchema),
  receiptNote: z.string().optional(),
  receivedAt: dateTimeSchema,
  receivedAtTimezone: z.string().optional(),
});

export type ReceiveShipmentRequest = z.infer<typeof receiveShipmentSchema>;

/**
 * Receive a shipment with the specified details.
 *
 * @param request - The shipment details to deliver
 * @param shipmentKey - The key from the shipment
 * @returns The received shipment
 */
export async function receiveShipment(
  shipmentKey: string,
  request: ReceiveShipmentRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}/receive`;
  const response = await api.post(url, request);

  const { data } = await parseResponse(
    receiveShipmentResponseSchema,
    response.data,
    {
      schemaName: 'ReceiveShipment',
      endpoint: url,
    }
  );
  return data;
}
